<script setup>
import { computed, markRaw, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useCamerasStore } from '@/store/cameras'
import { useCanvasStore } from '@/store/canvas'
import Konva from 'konva'

Konva.dragButtons = [0]
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import DeadAnimalIncidentPlugin from './canvas/CanvasPluginProviders/DeadAnimalIncidentPlugin.ts'
import RegionPlugin from './canvas/CanvasPluginProviders/RegionPlugin.ts'
import HumanTrackingPlugin from './canvas/CanvasPluginProviders/HumanTrackingPlugin.ts'
import TriggerDeadAnimal from '@/components/incidentTriggers/TriggerDeadAnimal.vue'
import { useRegionStore } from '@/store/regions'
import { useDownloadFile } from '@/composables/downloadFile'
import { useRouter } from 'vue-router'
import {
  deadAnimalIncidentTriggerPayload,
  setupLabelingHelper,
  showDeadAnimalIncidentTrigger,
  tearDownLabelingHelper,
} from '@/composables/canvasVisualLabelingHelper'

import { DateTime } from 'luxon'

import ElementProviderPicker from '@/components/canvas/Overlays/ElementProviderPicker.vue'
import BasicNavigation from '@/components/canvas/Overlays/BasicNavigation.vue'
import CanvasTimeJumper from '@/components/canvas/Overlays/CanvasTimeJumper.vue'
import CanvasTimePicker from '@/components/canvas/Overlays/CanvasTimePicker.vue'
import DeadAnimalOverlay from '@/components/canvas/PluginOverlays/DeadAnimalOverlay.vue'
import { useDisplay } from 'vuetify'
import { writeToClipboard } from '@/composables/clipboard.ts'
import { useInstanceStore } from '@/store/backendInstances'
import _ from 'lodash'
import { useLogsStore } from '@/store/logs'
import { getOrigin } from '@/composables/version.js'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import RegionsOverlay from '@/components/canvas/PluginOverlays/RegionsOverlay.vue'
import { canViewDeadAnimalsHelper, canViewLongposHelper, canViewPositionHelper, canViewRegionsHelper } from '@/composables/permissionHelper'
import DeadAnimalChip from '@/components/canvas/PluginOverlays/DeadAnimalChip.vue'
import RegionsChip from '@/components/canvas/PluginOverlays/RegionsChip.vue'
import { clampScaling, getCenter, getDistance, getRelativePointerPosition, detectTrackPad } from '@/composables/canvasHelper.js'
import { CanvasElementProvider } from '@/components/canvas/CanvasElementProvider.ts'
import { LongposPlugin } from '@/components/canvas/CanvasPluginProviders/LongposPlugin.ts'
import { PositionPlugin } from '@/components/canvas/CanvasPluginProviders/PosPlugin.ts'
import DeadAnimalIncidentClusterPlugin from '@/components/canvas/CanvasPluginProviders/DeadAnimalIncidentClusterPlugin.ts'
import LongPosChip from '@/components/canvas/PluginOverlays/LongPosChip.vue'
import PositionChip from '@/components/canvas/PluginOverlays/PositionChip.vue'
import HumanTrackingChip from '@/components/canvas/PluginOverlays/HumanTrackingChip.vue'

const props = defineProps({
  disableZoomButtons: { type: Boolean, default: false },
  canvasId: { type: String, default: null },
  overrideCanvas: { type: Object, default: null },
  draggable: { type: Boolean, default: true },
  scrollable: { type: Boolean, default: true },
  ctrlScrollable: { type: Boolean, default: true },
  pinchable: { type: Boolean, default: true },
  disableElementPicker: { type: Boolean, default: false },
  disableTimeJumper: { type: Boolean, default: false },
  disableNavigation: { type: Boolean, default: false },
  disableDeadAnimalOverlay: { type: Boolean, default: false },
  disableRegionsOverlay: { type: Boolean, default: false },
  disableHideUiButton: { type: Boolean, default: false },
  shrinkAllOverlaysByDefault: { type: Boolean, default: false },
  fullHeight: { type: Boolean, default: false },
})
const emit = defineEmits(['disableDeadAnimalLabeling'])
const timestamp = defineModel('timestamp', { type: DateTime, default: DateTime.now().startOf('second') })
const elementProvider = defineModel('elementProvider', { type: String, default: 'stillFrame' })
const pluginConfig = defineModel('pluginConfig', { type: Object, default: {} })
const baseRotation = defineModel('baseRotation', { type: Number, default: null })
const hideUIElements = defineModel('hideUIElements', { type: Boolean, default: false })

const canvasStore = useCanvasStore()
const { mobile, width, height } = useDisplay()
const router = useRouter()
const { t } = useI18n()
const toast = useToast()
const contextMenu = markRaw({})

const scaleBy = 1.05

const providers = {}
const plugins = ref({})
const showShareDialog = ref(false)
const showContextMenu = ref(false)
const enableTimeJumper = ref(false)

const stage = ref(null)
const initialSetup = ref(false)
const canvas = ref(null)
const elementProviderTooltipEnabled = ref(false)
const visualLabelingMode = ref(false)
const elementProviderTooltipConfig = ref({})
const pluginTooltipEnabled = ref(false)
const pluginTooltipConfig = ref({})
const elementPositions = ref({})
let basePosition = { x: 0, y: 0 }
const deadAnimalOverlayExpanded = ref(false)
const regionOverlayExpanded = ref(false)
const stayAtCurrentPositionForImageDownload = ref(true)
const imageDownloading = ref(false)
const showZoomInfoMessage = ref(undefined)
const isTrackpad = ref(null)

const urlSharing = ref({
  includeTimestamp: true,
  includeImageSource: true,
  includeRegions: true,
  includeDeadAnimalIncidents: true,
})

let group
const layers = {
  imageLayer: null,
  pluginLayers: null,
  visualLabelingLayer: null,
}
const stageConfig = ref({
  height: 100,
  width: 100,
  scale: {
    x: 1,
    y: 1,
  },
  position: {
    x: 0,
    y: 0,
  },
  draggable: props.draggable,
  pinch: {
    lastCenter: null,
    lastDist: null,
  },
})

const stageParent = ref(null)
const floatingRecenterButtonRef = ref(null)
const enableReCenterCanvasButton = ref(false)
const isFloatingButtonOverlapping = ref(false)

const stageHeight = computed(() => stageConfig.value?.height || 0)
const stageWidth = computed(() => stageConfig.value?.width || 0)

const stageParentHeight = ref(200)

const resizeStageParent = stageparentElement => {
  const computedWidth = parseFloat(getComputedStyle(stageparentElement).width)
  if (!canvas.value) {
    return computedWidth
  }

  const isDesktopLike = width.value > height.value
  // On Desktop-Like Systems, display Canvas in Landscape Mode (wide canvas)
  if (isDesktopLike && canvas.value.heightRatio > 1) {
    return computedWidth / canvas.value.heightRatio
  }
  // On Desktop-Like Systems, display Canvas in Portrait Mode (narrow canvas)
  if (isDesktopLike && canvas.value.heightRatio <= 1) {
    return computedWidth * canvas.value.heightRatio
  }

  // On Mobile-Like Systems, display Canvas in Portrait Mode
  return computedWidth * canvas.value.heightRatio
}

const canvasHeight = computed(() => {
  if (!stage.value || !canvas.value) return 0
  if (!props.fullHeight) {
    return parseFloat(stageParentHeight.value)
  }
  return stageHeight.value
})

const canvasWidth = computed(() => {
  if (!stage.value || !canvas.value) return 0
  return canvasHeight.value / canvas.value.heightRatio
})

const regionTypes = computed(() => {
  const types = useRegionStore().getRegionTypes()
  if (!types) return []
  return Object.keys(types)
})

const canViewDeadAnimals = computed(() => canViewDeadAnimalsHelper())
const canViewRegions = computed(() => canViewRegionsHelper())
const canViewLongPos = computed(() => canViewLongposHelper())
const canViewPosition = computed(() => canViewPositionHelper())
const sharingUrl = computed(() => {
  const params = { cameraCanvasId: props.canvasId }
  const query = {}
  if (urlSharing.value.includeTimestamp) query.timestamp = timestamp.value.toMillis()
  if (urlSharing.value.includeImageSource) query.elementProvider = elementProvider.value
  if (pluginConfig.value?.regions?.enabled && urlSharing.value.includeRegions) {
    query.showRegions = true
    query.regionType = pluginConfig.value.regions.regionType
    query.regionOpacity = parseInt(pluginConfig.value.regions.regionOpacity ?? 40)
  }
  if (pluginConfig.value?.deadAnimals?.enabled && urlSharing.value.includeDeadAnimalIncidents) {
    query.showDeadAnimals = true
    query.deadAnimalMarkerType = pluginConfig.value.deadAnimals.markerType
  }
  const route = router.resolve({ name: 'CameraCanvas', params, query })
  const url = new URL(route.href, getOrigin())
  return url.href
})

async function copySharingUrl() {
  const result = await writeToClipboard(sharingUrl.value)
  if (result) {
    toast.success(t('toast.copied_to_clipboard'))
  } else {
    toast.error(t('toast.copy_failed'))
  }
}

async function downloadImage() {
  imageDownloading.value = true
  await downloadAsImage(!stayAtCurrentPositionForImageDownload.value)
  imageDownloading.value = false
}

let reloadTimeout = null

function setupKonva() {
  if (layers.imageLayer) layers.imageLayer.destroy()
  layers.imageLayer = new Konva.Layer()
  layers.imageLayer.preventDefault(true)
  stage.value.getStage().add(layers.imageLayer)

  const scaleX = stageConfig.value.scale.x
  const scaleY = stageConfig.value.scale.y
  basePosition = {
    x: stageWidth.value / scaleX / 2,
    y: stageHeight.value / scaleY / 2,
  }
  stage.value.getStage().off('xChange')
  stage.value.getStage().off('yChange')
  stage.value.getStage().off('scaleChange')
  stage.value.getStage().off('wheel')

  function xChangeHandler() {
    disableElementProviderTooltip()
    updatePositions()
    const canvasWasMoved = stageConfig.value.position.x !== 0
    if (canvasWasMoved && enableReCenterCanvasButton.value === false) {
      enableReCenterCanvasButton.value = true
    }
  }

  function yChangeHandler() {
    disableElementProviderTooltip()
    updatePositions()
    const canvasWasMoved = stageConfig.value.position.y !== 0
    if (canvasWasMoved && enableReCenterCanvasButton.value === false) {
      enableReCenterCanvasButton.value = true
    }
  }

  function scaleChangeHandler() {
    if (reloadTimeout !== null) clearTimeout(reloadTimeout)
    reloadTimeout = setTimeout(() => {
      reloadSources()
    }, 500)
  }

  function wheelHandler(e) {
    if (!props.scrollable && !props.ctrlScrollable) return
    if (!props.scrollable && props.ctrlScrollable && !e.evt.ctrlKey) {
      if (!mobile.value && showZoomInfoMessage.value === undefined) {
        isTrackpad.value = detectTrackPad(e.evt)
        showZoomInfoMessage.value = true
        return
      }
      return
    }
    e.evt.preventDefault()
    showZoomInfoMessage.value = false

    // if the user is using a the mouse wheel to zoom, the recenter button should be visible
    if (enableReCenterCanvasButton.value === false) {
      enableReCenterCanvasButton.value = true
    }

    const oldScale = stageConfig.value.scale.x
    const pointer = stage.value.getStage().getPointerPosition()

    const mousePointTo = {
      x: (pointer.x - stage.value?.getStage()?.x()) / oldScale,
      y: (pointer.y - stage.value?.getStage()?.y()) / oldScale,
    }

    // how to scale? Zoom in? Or zoom out?
    let direction = e.evt.deltaY > 0 ? -1 : 1
    if (e.evt.deltaX !== 0) {
      direction = e.evt.deltaX > 0 ? 1 : -1
    }
    // when we zoom on trackpad, e.evt.ctrlKey is true
    // in that case lets revert direction
    if (e.evt.altKey) {
      direction = -direction
    }

    const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy

    stageConfig.value.scale = { x: newScale, y: newScale }

    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    }

    try {
      stageConfig.value.position = newPos
    } catch (e) {
      useLogsStore().addLogEntry({ message: 'Error while zooming canvas', error: e, tag: 'canvas', level: 'ERROR' })
    }
  }

  function touchMoveHandler(e) {
    if (e.touches.length < 2) return

    // if the user is using a 2-finger gesture, the recenter button should be visible
    if (enableReCenterCanvasButton.value === false) {
      enableReCenterCanvasButton.value = true
    }

    e.preventDefault()
    const touch1 = e.touches[0]
    const touch2 = e.touches[1]

    // if the stage was under Konva's drag&drop
    // we need to stop it, and implement our own pan logic with two pointers
    if (stage.value.getStage().isDragging()) {
      stage.value.getStage().stopDrag()
    }

    const container = stage.value.getStage().container()
    const p1 = getRelativePointerPosition(container, touch1)
    const p2 = getRelativePointerPosition(container, touch2)

    if (!stageConfig.value.pinch.lastCenter) {
      stageConfig.value.pinch.lastCenter = getCenter(p1, p2)
      return
    }
    const newCenter = getCenter(p1, p2)

    const dist = getDistance(p1, p2)

    if (!stageConfig.value.pinch.lastDist) {
      stageConfig.value.pinch.lastDist = dist
    }

    // local coordinates of center point
    const pointTo = {
      x: (newCenter.x - stage.value?.getStage()?.x()) / stageConfig.value.scale.x,
      y: (newCenter.y - stage.value?.getStage()?.y()) / stageConfig.value.scale.y,
    }

    const scale = clampScaling(stageConfig.value.scale.x * (dist / stageConfig.value.pinch.lastDist))

    stageConfig.value.scale = { x: scale, y: scale }

    // translation with 2 finger: calculate new position of the stage
    const dx = newCenter.x - stageConfig.value.pinch.lastCenter.x
    const dy = newCenter.y - stageConfig.value.pinch.lastCenter.y

    stageConfig.value.position = {
      x: newCenter.x - pointTo.x * scale + dx,
      y: newCenter.y - pointTo.y * scale + dy,
    }

    stageConfig.value.pinch.lastDist = dist
    stageConfig.value.pinch.lastCenter = newCenter
  }

  stage.value.getStage().on('xChange', xChangeHandler)
  stage.value.getStage().on('yChange', yChangeHandler)
  stage.value.getStage().on('scaleChange', scaleChangeHandler)
  stage.value.getStage().on('wheel', wheelHandler)

  // Handle 2-finger gestures for zooming and panning on mobile devices
  if (props.pinchable) {
    const stageEl = stage.value.getStage().getContent()
    stageEl.addEventListener('touchmove', touchMoveHandler, { passive: false })

    stage.value.getStage().on('touchend', function () {
      stageConfig.value.pinch.lastCenter = null
      stageConfig.value.pinch.lastDist = null
    })
  }

  window.addEventListener('resize', () => {
    resize()
  })
  group = new Konva.Group()
  group.preventDefault(false)
}

function getTimestamp() {
  return timestamp.value
}

function resize() {
  const container = document.querySelector('#stage-parent')
  if (!container) return
  const containerWidth = container.offsetWidth
  const containerHeight = container.offsetHeight
  stageConfig.value.width = containerWidth
  stageConfig.value.height = containerHeight
  automaticResize()
}

function buildElementPositions() {
  for (const camera of canvas.value.cameras) {
    elementPositions.value[camera.cameraId] = {
      cameraId: camera.cameraId,
      x: camera.xPosition * canvasWidth.value,
      y: camera.yPosition * canvasHeight.value,
      width: camera.relativeWidth * canvasWidth.value,
      height: (camera.relativeWidth * canvasWidth.value) / 1.78,
      rotation: camera.rotation,
    }
  }
}

function automaticResize() {
  const r = baseRotation.value
  const canvasBounds = getCanvasBounds()
  let effectiveCanvasWidth
  let effectiveCanvasHeight
  if (r === 0 || r === 180 || r === 360) {
    effectiveCanvasWidth = canvasBounds.maxX - canvasBounds.minX
    effectiveCanvasHeight = canvasBounds.maxY - canvasBounds.minY
  }
  if (r === 90 || r === 270) {
    effectiveCanvasWidth = canvasBounds.maxY - canvasBounds.minY
    effectiveCanvasHeight = canvasBounds.maxX - canvasBounds.minX
  }
  const possibleScaleX = stageWidth.value / effectiveCanvasWidth
  const possibleScaleY = stageHeight.value / effectiveCanvasHeight

  const newScale = Math.min(possibleScaleX, possibleScaleY)

  stageConfig.value.scale = { x: newScale, y: newScale }
  reloadSources()
}

function rotateAndResize() {
  const r = baseRotation.value
  const canvasBounds = getCanvasBounds()

  group.offset({ x: canvasBounds.xCenter, y: canvasBounds.yCenter })
  group.rotation(r)

  if (plugins.value)
    Object.values(plugins.value).forEach(plugin => {
      plugin.group.offset({ x: canvasBounds.xCenter, y: canvasBounds.yCenter })
      plugin.group.rotation(r)
    })

  automaticResize()
  goToCenter()
  return reloadSources()
}

function updatePositions() {
  Object.values(providers).forEach(provider => {
    provider.updatePosition()
  })

  stageConfig.value.position = {
    x: stage.value.getStage().x(),
    y: stage.value.getStage().y(),
  }
}

function updateProviderTypes() {
  Object.values(providers).forEach(provider => {
    provider.setProviderType(elementProvider.value)
  })
}

function updateProviderTimestamps() {
  Object.values(providers).forEach(provider => {
    provider.updateTimestamp(timestamp.value.toMillis())
  })
}

function updatePluginConfig(pluginType) {
  if (!plugins.value[pluginType]) return addPlugin(pluginType)
  plugins.value[pluginType].updateConfig(pluginConfig.value[pluginType])
}

function disablePlugin(pluginType) {
  if (!plugins.value[pluginType]) return
  plugins.value[pluginType].disable()
}

function enablePlugin(pluginType) {
  if (!plugins.value[pluginType]) return addPlugin(pluginType)
  plugins.value[pluginType].enable()
}

function addPlugin(pluginType) {
  const config = pluginConfig.value[pluginType]
  let pluginClass = null
  switch (pluginType) {
    case 'deadAnimals':
      pluginClass = DeadAnimalIncidentPlugin
      break
    case 'regions':
      pluginClass = RegionPlugin
      break
    case 'longpos':
      pluginClass = LongposPlugin
      break
    case 'position':
      pluginClass = PositionPlugin
      break
    case 'deadAnimalClusters':
      pluginClass = DeadAnimalIncidentClusterPlugin
      break
    case 'humanTracking':
      pluginClass = HumanTrackingPlugin
      break
  }

  if (!pluginClass) return
  const plugin = new pluginClass(
    {
      enabled: config.enabled,
      canvasId: props.canvasId,
      canvas: canvas.value,
      stage: stage.value,
      group: group,
      router: router,
      elementPositions: elementPositions.value,
      timestamp: timestamp.value,
      emit: emit,
      contextMenuHandler: c => {
        contextMenu.value = c
        showContextMenu.value = true
      },
      tooltipEnable: enablePluginProviderTooltip,
      tooltipDisable: disablePluginProviderTooltip,
      stageWidth: stageWidth.value,
      stageHeight: stageHeight.value,
      pluginTooltipEnabled: pluginTooltipEnabled,
      pluginTooltipConfig: pluginTooltipConfig,
    },
    config
  )

  plugins.value[pluginType] = plugin
  if (!layers.pluginLayers) layers.pluginLayers = {}
  layers.pluginLayers[pluginType] = plugin.layer
  stage.value.getStage().add(plugin.layer)

  const canvasBounds = getCanvasBounds()
  plugins.value[pluginType].group.offset({ x: canvasBounds.xCenter, y: canvasBounds.yCenter })
  plugins.value[pluginType].group.rotation(baseRotation.value)

  plugins.value[pluginType].group.position(basePosition)
}

function updatePluginTimestamps() {
  Object.values(plugins.value).forEach(plugin => {
    plugin.updateTimestamp(timestamp.value)
  })
}

let elementTooltipTimeout = null
let pluginTooltipTimeout = null

function enableElementProviderTooltip({ x, y, text }) {
  clearTimeout(elementTooltipTimeout)
  clearTimeout(pluginTooltipTimeout)
  pluginTooltipEnabled.value = false

  const adjustedY = y + window.scrollY
  elementProviderTooltipConfig.value = { x, y: adjustedY, text }
  elementProviderTooltipEnabled.value = true
}

function enablePluginProviderTooltip({ x, y, text }) {
  //clearTimeout(elementTooltipTimeout)
  clearTimeout(pluginTooltipTimeout)
  elementProviderTooltipEnabled.value = false

  const adjustedY = y + window.scrollY
  pluginTooltipConfig.value = { x, y: adjustedY, text }
  pluginTooltipEnabled.value = true
}

function disableElementProviderTooltip() {
  clearTimeout(elementTooltipTimeout)
  elementTooltipTimeout = setTimeout(() => {
    elementProviderTooltipEnabled.value = false
  }, 100)
}

function disablePluginProviderTooltip() {
  clearTimeout(pluginTooltipTimeout)
  pluginTooltipTimeout = setTimeout(() => {
    pluginTooltipEnabled.value = false
  }, 100)
}

async function loadProvider(cameraId) {
  providers[cameraId] = new CanvasElementProvider(
    elementProvider.value,
    timestamp.value.toMillis(),
    elementPositions.value[cameraId].cameraId,
    elementPositions.value[cameraId].width,
    elementPositions.value[cameraId].height,
    stageConfig.value.scale.y,
    elementPositions.value[cameraId].x,
    elementPositions.value[cameraId].y,
    elementPositions.value[cameraId].rotation,
    group,
    props.draggable,
    enableElementProviderTooltip,
    disableElementProviderTooltip
  )
}

async function loadProviders() {
  if (layers.imageLayer) layers.imageLayer.removeChildren()
  if (providers) Object.values(providers).forEach(provider => provider.destroyElement())
  for (const key in Object.keys(providers)) delete providers[key]
  layers.imageLayer.add(group)

  const promises = Object.values(elementPositions.value).map(elementPosition => {
    return loadProvider(elementPosition.cameraId)
  })
  await Promise.all(promises)
  await reloadSources()
}

async function loadCameras() {
  return loadProviders()
}

/**
 * Handles zooming in and out of the canvas
 * @param {string} direction - 'in' or 'out'
 */
async function zoom(direction = 'in') {
  const SCALE_FACTOR = direction === 'in' ? 0.5 : -0.5

  const centerX = stageWidth.value / 2
  const centerY = stageHeight.value / 2

  const oldScale = stageConfig.value.scale.x
  const newScale = clampScaling(oldScale * (1 + SCALE_FACTOR))

  const pointer = {
    x: (centerX - stageConfig.value.position.x) / oldScale,
    y: (centerY - stageConfig.value.position.y) / oldScale,
  }

  stageConfig.value.scale = { x: newScale, y: newScale }

  const newPos = {
    x: centerX - pointer.x * newScale,
    y: centerY - pointer.y * newScale,
  }

  stageConfig.value.position = newPos
}

async function zoomToSpecificPosition({ marker }) {
  const position = marker.getAbsolutePosition(stage.value.getStage())

  let elementSize = { width: marker.width(), height: marker.height() }

  // This prevents division by zero errors on both axis. Consider that only if both values are zero,
  // the scaling will become infinity and the canvas breaks. If only one of the two values is zero,
  // the scaling goes to the minimum of the other axis.
  // This should never happen anymore, because the size of width and height will be checked before
  // setting up a dead animal incident. But just in case!
  if (elementSize.width === 0 && elementSize.height === 0) {
    elementSize = { width: 1, height: 1 }
  }

  const maxScaleX = stageWidth.value / (elementSize.width * 8)
  const maxScaleY = stageHeight.value / (elementSize.height * 8)
  const scaling = Math.min(maxScaleX, maxScaleY)

  const newX = -position.x * scaling + stageWidth.value / 2 - (elementSize.width * scaling) / 2
  const newY = -position.y * scaling + stageHeight.value / 2 - (elementSize.height * scaling) / 2

  stageConfig.value.scale = { x: scaling, y: scaling }
  stageConfig.value.position = { x: newX, y: newY }
}

async function reloadSources() {
  for (const provider of Object.values(providers)) {
    provider.updateScale(stageConfig.value.scale.y)
  }
}

function goToCenter() {
  resize()
  stageConfig.value.position = { x: 0, y: 0 }

  const scaleX = stageConfig.value.scale.x
  const scaleY = stageConfig.value.scale.y

  basePosition = {
    x: stageWidth.value / scaleX / 2,
    y: stageHeight.value / scaleY / 2,
  }
  group.position(basePosition)

  if (plugins.value)
    Object.values(plugins.value).forEach(plugin => {
      plugin.group.position(basePosition)
    })

  enableReCenterCanvasButton.value = false
}

function getCanvasBounds() {
  const rect = group.getClientRect({ skipTransform: true })
  const minX = rect.x
  const minY = rect.y
  const maxX = rect.x + rect.width
  const maxY = rect.y + rect.height
  return { xCenter: (maxX - minX) / 2, yCenter: (maxY - minY) / 2, minX: minX, minY: minY, maxX: maxX, maxY: maxY }
}

async function downloadAsImage(recenter) {
  let boundingBox = null
  const clientRect = layers.imageLayer.getClientRect()
  if (recenter) {
    boundingBox = layers.imageLayer.getClientRect()
  } else {
    boundingBox = {
      x: Math.max(0, clientRect.x),
      y: Math.max(0, clientRect.y),
    }
    boundingBox.width = Math.min(clientRect.width, stageWidth.value - boundingBox.x)
    boundingBox.height = Math.min(clientRect.height, stageHeight.value - boundingBox.y)
  }
  const img = await stage.value.getStage().toBlob({
    x: boundingBox.x,
    y: boundingBox.y,
    width: boundingBox.width,
    height: boundingBox.height,
    mimeType: 'image/png',
  })
  const filename = `${useInstanceStore().selectedInstance.name}_${timestamp.value.toFormat('yyyy-MM-dd_HH-mm-ss')}.png`
  useDownloadFile(img, filename)
}

function enableVisualLabelingMode(type) {
  visualLabelingMode.value = true

  Object.values(providers).forEach(provider => provider.enableBorders())
  for (const layer of Object.values(layers.pluginLayers)) {
    layer.listening(false)
  }

  if (layers.visualLabelingLayer) layers.visualLabelingLayer.destroy()

  layers.visualLabelingLayer = new Konva.Layer()

  setupLabelingHelper({
    getTimestamp: getTimestamp,
    labelingType: type,
    providers: providers,
    imageLayer: layers.imageLayer,
    stage: stage.value,
    labelingLayer: layers.visualLabelingLayer,
  })
  layers.visualLabelingLayer.preventDefault(false)
  stage.value.getStage().add(layers.visualLabelingLayer)
}

function disableVisualLabelingMode() {
  visualLabelingMode.value = false
  Object.values(providers).forEach(provider => provider.disableBorders())
  tearDownLabelingHelper()

  for (const layer of Object.values(layers.pluginLayers)) {
    layer.listening(true)
  }

  layers.visualLabelingLayer.destroy()
  delete layers.visualLabelingLayer
  emit('disableDeadAnimalLabeling')
}

watch(
  () => baseRotation.value,
  () => {
    if (!initialSetup.value) return
    rotateAndResize()
  }
)

watch(timestamp, async (value, oldValue) => {
  if (!initialSetup.value) return
  if (value.startOf('second').toMillis() === oldValue.startOf('second').toMillis()) return
  updateProviderTimestamps()
  updatePluginTimestamps()
})

const loading = computed(() => {
  return (
    imageProvidersLoading.value ||
    !initialSetup.value ||
    plugins.value?.deadAnimals?.reactiveProperties.loading ||
    plugins.value?.regions?.reactiveProperties.loading
  )
})

const imageProvidersLoading = computed(() => {
  return false
})

const loadingText = computed(() => {
  if (!loading.value) return ''
  if (!initialSetup.value) return t('canvas.loading_initial_setup')
  if (imageProvidersLoading.value) return t('canvas.loading_image_providers')
  if (plugins.value?.deadAnimals?.reactiveProperties.loading) return t('canvas.loading_dead_animals')
  if (plugins.value?.regions?.reactiveProperties.loading) return t('canvas.loading_regions')

  return t('canvas.loading')
})

function updatePlugins(newConfig = {}, oldConfig = {}) {
  try {
    if (!initialSetup.value) return

    const removedPlugins = Object.keys(oldConfig || {}).filter(key => !Object.keys(newConfig || {}).includes(key))
    const addedPlugins = Object.keys(newConfig || {}).filter(key => !Object.keys(oldConfig || {}).includes(key))
    const updatedPlugins = Object.keys(newConfig || {}).filter(key => Object.keys(oldConfig || {}).includes(key))

    for (const pluginType of removedPlugins) {
      disablePlugin(pluginType)
    }
    for (const pluginType of addedPlugins) {
      if (newConfig[pluginType].enabled) enablePlugin(pluginType)
    }
    for (const pluginType of updatedPlugins) {
      if (!newConfig[pluginType].enabled) {
        disablePlugin(pluginType)
      } else {
        updatePluginConfig(pluginType)
        enablePlugin(pluginType)
      }
    }
  } catch (e) {
    useLogsStore().addLogEntry({ message: 'Error while updating plugins', error: e, tag: 'canvas', level: 'ERROR' })
  }
}

function forceUpdateDeadAnimalPlugin() {
  if (!plugins.value['deadAnimals']) return
  plugins.value['deadAnimals'].forceUpdate()
}

function updateDeadAnimalOverlayExpanded(value) {
  deadAnimalOverlayExpanded.value = value
  if (value && mobile.value) {
    enableTimeJumper.value = false
  }
}

function updateRegionOverlayExpanded(value) {
  regionOverlayExpanded.value = value
  if (value && mobile.value) {
    enableTimeJumper.value = false
  }
}

function updateTimePickerExpanded(value) {
  if (value && mobile.value) {
    deadAnimalOverlayExpanded.value = false
    regionOverlayExpanded.value = false
  }
}

function showTimeJumper() {
  enableTimeJumper.value = !enableTimeJumper.value
  if (mobile.value) {
    deadAnimalOverlayExpanded.value = false
    regionOverlayExpanded.value = false
  }
}

function toggleDeadAnimalOverlay(hideIncidentOverlay = false) {
  const isEnabled = !pluginConfig.value.deadAnimals?.enabled ?? true
  pluginConfig.value = {
    ...pluginConfig.value,
    deadAnimals: {
      ...pluginConfig.value.deadAnimals,
      enabled: hideIncidentOverlay || isEnabled,
      markerType: pluginConfig.value.deadAnimals?.markerType ?? 'circle',
      hideIncidentOverlay: hideIncidentOverlay,
    },
  }

  if (isEnabled) {
    updateDeadAnimalOverlayExpanded(true)
    return
  }
  updateDeadAnimalOverlayExpanded(false)
}

function toggleRegionsOverlay(hideRegionsOverlay = false) {
  const isEnabled = !pluginConfig.value.regions?.enabled ?? true
  pluginConfig.value = {
    ...pluginConfig.value,
    regions: {
      ...pluginConfig.value.regions,
      enabled: hideRegionsOverlay || isEnabled,
      regionType: pluginConfig.value.regions?.regionType ?? regionTypes.value[0],
      regionOpacity: pluginConfig.value.regions?.regionOpacity ?? 40,
      hideRegionsOverlay: hideRegionsOverlay,
    },
  }

  if (isEnabled) {
    updateRegionOverlayExpanded(true)
    return
  }
  updateRegionOverlayExpanded(false)
}

function toggleLongposOverlay() {
  const isEnabled = !pluginConfig.value.longpos?.enabled ?? true
  pluginConfig.value = {
    ...pluginConfig.value,
    longpos: {
      ...pluginConfig.value.longpos,
      enabled: isEnabled,
    },
  }
}

function togglePositionOverlay() {
  const isEnabled = !pluginConfig.value.position?.enabled ?? true
  pluginConfig.value = {
    ...pluginConfig.value,
    position: {
      ...pluginConfig.value.position,
      enabled: isEnabled,
    },
  }
}

function toggleHumanTrackingOverlay() {
  const isEnabled = !pluginConfig.value.humanTracking?.enabled ?? true
  pluginConfig.value = {
    ...pluginConfig.value,
    humanTracking: {
      ...pluginConfig.value.humanTracking,
      enabled: isEnabled,
    },
  }
}

/**
 * Checks if the floating re-center button overlaps with the canvas.
 * This is done by comparing the bounding rectangles of the button and the canvas parent.
 * If they overlap, the button will be visible, otherwise it will be hidden.
 */
function checkFloatingButtonOverlap() {
  if (!stageParent.value || !floatingRecenterButtonRef.value) return

  const stageParentRect = stageParent.value.getBoundingClientRect()
  const buttonRect = floatingRecenterButtonRef.value?.$el.getBoundingClientRect()

  const overlapping =
    stageParentRect.left < buttonRect.right &&
    stageParentRect.right > buttonRect.left &&
    stageParentRect.top < buttonRect.bottom &&
    stageParentRect.bottom > buttonRect.top

  isFloatingButtonOverlapping.value = overlapping
  return overlapping
}

watch(
  pluginConfig,
  (value, oldValue) => {
    updatePlugins(value, oldValue)
  },
  { deep: true, immediate: true }
)

watch(
  () => elementProvider.value,
  async () => {
    if (!initialSetup.value) return
    updateProviderTypes()
  }
)

const baseCanvas = computed(() => {
  return useCanvasStore().getBaseCanvas()
})

const canvasId = computed(() => {
  if (props.canvasId) return props.canvasId
  if (props.overrideCanvas) return props.overrideCanvas.cameraCanvasId
  if (baseCanvas.value) return baseCanvas.value.cameraCanvasId
  return null
})

watch(
  [canvasId, baseCanvas, () => !!stage.value],
  async value => {
    if (!value[2]) return
    if (!value[0]) return
    if (value[1] && value[1].cameraCanvasId === value[0]) {
      canvas.value = value[1]
    } else {
      canvas.value = await canvasStore.loadSpecificCanvas(value[0])
    }
  },
  { immediate: true, deep: true }
)

watch(
  [canvas],
  async (value, oldValue) => {
    if (!canvas.value) return
    if (_.isEqual(oldValue, value)) return
    initialSetup.value = false
    setupKonva()
    stageConfig.value.width = stageWidth.value
    stageConfig.value.height = stageHeight.value
    buildElementPositions()
    initialSetup.value = true
    await loadCameras()
    updatePlugins(pluginConfig.value)
    await rotateAndResize()
    automaticResize()
    goToCenter()
  },
  { immediate: true, deep: true }
)

// automatically hide showZoomInfoMessage after 2 seconds
watch(showZoomInfoMessage, () => {
  if (mobile.value) return

  if (showZoomInfoMessage.value) {
    setTimeout(() => {
      showZoomInfoMessage.value = undefined
    }, 2000)
  }
})

onMounted(async () => {
  const el = document.querySelector('#stage-parent')
  if (el) {
    const resizeObserver = new ResizeObserver(() => {
      stageParentHeight.value = resizeStageParent(el)

      automaticResize()
      goToCenter()
      checkFloatingButtonOverlap()
    })
    resizeObserver.observe(el)
  }

  window.addEventListener('scroll', checkFloatingButtonOverlap, { passive: true })

  if (props.shrinkAllOverlaysByDefault) {
    updateDeadAnimalOverlayExpanded(false)
    updateTimePickerExpanded(false)
    enableTimeJumper.value = false
  }
  if (baseRotation.value === null) {
    const display = useDisplay()
    const width = display.width.value
    const height = display.height.value
    if (width > height) baseRotation.value = 270
    else baseRotation.value = 0
  }
  if (!props.scrollable && !props.ctrlScrollable && !props.pinchable) {
    stageConfig.value.draggable = false
  }
  useCanvasStore().keepBaseCanvasLoaded()
  useRegionStore().keepRegionsLoaded()
  await useCamerasStore().ensureCamerasLoaded()
})

onBeforeUnmount(() => {
  if (providers) Object.values(providers).forEach(provider => provider.destroyElement())
  if (plugins.value) Object.values(plugins.value).forEach(plugin => plugin.destroyPlugin())
  if (layers.visualLabelingLayer) layers.visualLabelingLayer.destroy()
  if (layers.pluginLayers) Object.values(layers.pluginLayers).forEach(layer => layer.destroy())
  if (layers.imageLayer) layers.imageLayer.destroy()
  if (stage.value) stage.value.getStage().destroy()

  window.removeEventListener('resize', () => {
    resize()
  })
  window.removeEventListener('scroll', checkFloatingButtonOverlap)

  stage.value
    ?.getStage()
    ?.getContent()
    ?.removeEventListener('touchmove', () => {})
})

defineExpose({
  goToCenter,
  automaticResize,
  downloadAsImage,
})
</script>

<template>
  <div
    id="stage-parent"
    ref="stageParent"
    class="overflow-hidden rounded-lg"
    :style="{
      width: '100%',
      height: props.fullHeight ? '100%' : `${stageParentHeight}px`,
    }"
  >
    <v-stage
      id="stage"
      ref="stage"
      :config="stageConfig"
    />

    <v-btn
      v-if="props.disableNavigation && !hideUIElements"
      :id="mobile ? 'mobile-floating-recenter-button' : 'floating-recenter-button'"
      ref="floatingRecenterButtonRef"
      class="rounded-circle"
      :class="{
        'position-fixed': mobile,
        'position-absolute': !mobile,
        hidden: !isFloatingButtonOverlapping || !enableReCenterCanvasButton,
      }"
      icon="mdi-target"
      variant="flat"
      color="primary"
      @click="goToCenter"
    />

    <v-fade-transition>
      <div
        v-if="showZoomInfoMessage"
        class="rounded-lg position-absolute"
        style="width: 100%; height: 100%; top: 0; left: 0; z-index: 9999; pointer-events: none; background-color: #00000040"
      >
        <v-alert
          class="px-6 position-absolute"
          style="top: 50%; left: 50%; transform: translate(-50%, -50%)"
          variant="flat"
          icon="mdi-information"
        >
          <i18n-t
            v-if="isTrackpad"
            keypath="canvas.zoom_info_message_trackpad"
            tag="p"
          >
            <template #trackpad>
              <kbd class="kbd">
                <v-icon
                  class="me-1"
                  icon="mdi-gesture-pinch"
                  size="small"
                />
                {{ $t('canvas.zoom_trackpad') }}
              </kbd>
            </template>
          </i18n-t>
          <i18n-t
            v-else
            keypath="canvas.zoom_info_message"
            tag="p"
          >
            <template #ctrl>
              <kbd class="kbd">
                {{ $t('canvas.zoom_ctrl') }}
              </kbd>
            </template>
            <template #scroll>
              <kbd class="kbd">
                <v-icon
                  class="me-1"
                  icon="mdi-mouse-move-up"
                  size="small"
                />
                {{ $t('canvas.zoom_scroll') }}
              </kbd>
            </template>
          </i18n-t>
        </v-alert>
      </div>
    </v-fade-transition>

    <GenericCustomDialog
      v-model="showShareDialog"
      max-width="600px"
    >
      <template #headerIcon>
        <v-icon icon="mdi-share-variant" />
      </template>
      <template #title>
        {{ $t('general_interface.buttons.share') }}
      </template>
      <template #body>
        <v-text-field
          :model-value="sharingUrl"
          prepend-icon="mdi-link"
          class="my-2"
          variant="outlined"
          readonly
          hide-details
        >
          <template #append-inner>
            <v-btn
              variant="flat"
              class="rounded-pill"
              color="primary"
              @click="copySharingUrl"
            >
              {{ $t('general_interface.buttons.copy') }}
            </v-btn>
          </template>
        </v-text-field>

        <v-checkbox
          v-model="urlSharing.includeTimestamp"
          prepend-icon="mdi-timer"
          :label="$t('canvas.sharing.include_timestamp')"
          hide-details
        />

        <v-checkbox
          v-model="urlSharing.includeImageSource"
          prepend-icon="mdi-image"
          :label="$t('canvas.sharing.include_image_source')"
          hide-details
        />

        <v-checkbox
          v-if="pluginConfig?.regions?.enabled"
          v-model="urlSharing.includeRegions"
          prepend-icon="mdi-shape"
          :label="$t('canvas.sharing.include_regions')"
          hide-details
        />

        <v-checkbox
          v-if="pluginConfig?.deadAnimals?.enabled"
          v-model="urlSharing.includeDeadAnimalIncidents"
          prepend-icon="mdi-skull-crossbones"
          :label="$t('canvas.sharing.include_dead_animal_incidents')"
          hide-details
        />

        <v-divider
          class="my-4"
          thickness="2"
        />

        <v-card-title>{{ $t('canvas.sharing.image_title') }}</v-card-title>
        <v-checkbox
          v-model="stayAtCurrentPositionForImageDownload"
          prepend-icon="mdi-crop-free"
          :label="$t('canvas.sharing.use_current_image_section')"
          hide-details
        />
      </template>
      <template #actions>
        <v-btn
          prepend-icon="mdi-download"
          variant="flat"
          color="primary"
          :loading="imageDownloading"
          @click="downloadImage"
        >
          {{ $t('canvas.sharing.download_image') }}
        </v-btn>
      </template>
    </GenericCustomDialog>

    <v-tooltip
      location="bottom"
      scroll-strategy="close"
    >
      <template #activator="{ props: tooltipProps }">
        <v-btn
          v-if="!disableHideUiButton"
          v-bind="tooltipProps"
          class="position-absolute rounded-pill"
          style="top: 16px; right: 16px; z-index: 999"
          variant="elevated"
          :icon="hideUIElements ? 'mdi-eye-off' : 'mdi-eye'"
          :color="hideUIElements ? 'primary' : 'default'"
          @click="hideUIElements = !hideUIElements"
        />
      </template>
      <span>
        {{ hideUIElements ? $t('canvas.settings.overlay.show_ui') : $t('canvas.settings.overlay.hide_ui') }}
      </span>
    </v-tooltip>

    <BasicNavigation
      v-if="!disableNavigation && !hideUIElements"
      id="basic_nav"
      v-model:base-rotation="baseRotation"
      :include-zoom="!disableZoomButtons"
      @click:recenter="
        () => {
          automaticResize()
          goToCenter()
        }
      "
      @click:zoom-in="zoom('in')"
      @click:zoom-out="zoom('out')"
      @open-share-dialog="showShareDialog = true"
    />

    <v-sheet
      v-if="loading"
      variant="elevated"
      class="position-absolute pt-1 rounded-lg justify-center"
      style="top: 8px; right: 50%; transform: translate(+50%); z-index: 10"
    >
      <v-label class="mx-2">
        {{ loadingText }}
      </v-label>
      <v-progress-linear :indeterminate="true" />
    </v-sheet>

    <v-slide-x-transition>
      <div
        class="position-absolute rounded-pill d-flex align-center ga-4"
        style="top: 16px; left: 16px"
      >
        <CanvasTimePicker
          v-if="!disableTimeJumper && !hideUIElements"
          v-model="timestamp"
          style="width: 300px"
          :enable-time-jumper="enableTimeJumper"
          @update:enable-time-jumper="showTimeJumper"
        />

        <v-divider
          v-if="
            !disableTimeJumper &&
            !disableDeadAnimalOverlay &&
            !hideUIElements &&
            canViewDeadAnimals &&
            !disableRegionsOverlay &&
            canViewRegions &&
            !mobile
          "
          vertical
          thickness="2"
          color="primary"
        />

        <!-- dead animal fast chip, regions chip -->
        <div
          v-if="!mobile"
          class="d-flex align-center ga-2"
        >
          <DeadAnimalChip
            v-if="!disableDeadAnimalOverlay && !hideUIElements && canViewDeadAnimals"
            :enabled="pluginConfig['deadAnimals']?.enabled"
            @show-dead-animal-overlay="toggleDeadAnimalOverlay"
          />

          <RegionsChip
            v-if="!disableRegionsOverlay && !hideUIElements && canViewRegions"
            :enabled="pluginConfig['regions']?.enabled"
            @show-regions-overlay="toggleRegionsOverlay"
          />

          <LongPosChip
            v-if="canViewLongPos"
            :enabled="pluginConfig['longpos']?.enabled"
            @show-longpos-overlay="toggleLongposOverlay"
          />
          <PositionChip
            v-if="canViewPosition"
            :enabled="pluginConfig['position']?.enabled"
            @show-position-overlay="togglePositionOverlay"
          />
          <HumanTrackingChip
            v-if="canViewLongPos"
            :enabled="pluginConfig['humanTracking']?.enabled"
            @show-human-tracking-overlay="toggleHumanTrackingOverlay"
          />
        </div>
      </div>
    </v-slide-x-transition>

    <v-slide-x-transition>
      <div
        v-if="mobile"
        class="position-absolute d-flex align-center ga-2"
        style="top: 88px; left: 16px"
      >
        <DeadAnimalChip
          v-if="!disableDeadAnimalOverlay && !hideUIElements && canViewDeadAnimals"
          :enabled="pluginConfig['deadAnimals']?.enabled"
          @show-dead-animal-overlay="toggleDeadAnimalOverlay"
        />

        <RegionsChip
          v-if="!disableRegionsOverlay && !hideUIElements && canViewRegions"
          :enabled="pluginConfig['regions']?.enabled"
          @show-regions-overlay="toggleRegionsOverlay"
        />
      </div>
    </v-slide-x-transition>

    <CanvasTimeJumper
      v-if="enableTimeJumper && !disableTimeJumper && !hideUIElements"
      v-model="timestamp"
      @close="enableTimeJumper = false"
    />

    <ElementProviderPicker
      v-if="!disableElementPicker && !hideUIElements"
      :element-provider="elementProvider"
      @update:element-provider="elementProvider = $event"
      @open-share-dialog="showShareDialog = true"
    />

    <DeadAnimalOverlay
      v-if="!hideUIElements"
      :plugin-config="pluginConfig['deadAnimals']"
      :plugin="plugins['deadAnimals']"
      :expanded="deadAnimalOverlayExpanded"
      :visual-labeling-mode-enabled="visualLabelingMode"
      @update:expanded="updateDeadAnimalOverlayExpanded"
      @update:plugin-config="pluginConfig = { ...pluginConfig, deadAnimals: $event }"
      @zoom-to-specific-position="zoomToSpecificPosition"
      @enable-visual-labeling-mode="enableVisualLabelingMode"
      @disable-visual-labeling-mode="disableVisualLabelingMode"
      @toggle-dead-animal-overlay="toggleDeadAnimalOverlay"
    />

    <RegionsOverlay
      v-if="!hideUIElements && pluginConfig.regions?.enabled"
      :plugin-config="pluginConfig['regions']"
      :plugin="plugins['regions']"
      @update:plugin-config="pluginConfig = { ...pluginConfig, regions: $event }"
      @toggle-regions-overlay="toggleRegionsOverlay"
    />

    <v-menu
      v-if="showContextMenu && !hideUIElements"
      v-model="showContextMenu"
      close-delay="0"
      :close-on-content-click="false"
      :close-on-click="true"
      :style="showContextMenu ? `top: ${contextMenu.value.y}px; left: ${contextMenu.value.x}px;` : undefined"
      z-index="100"
      offset-y
    >
      <component
        :is="contextMenu.value.component"
        v-bind="contextMenu.value.props"
        v-on="contextMenu.value.on"
      />
    </v-menu>

    <v-tooltip
      v-if="elementProviderTooltipEnabled && !hideUIElements"
      v-model="elementProviderTooltipEnabled"
      :style="elementProviderTooltipEnabled ? `top: ${elementProviderTooltipConfig.y}px; left: ${elementProviderTooltipConfig.x}px;` : undefined"
      :text="elementProviderTooltipConfig.text"
    />

    <v-tooltip
      v-if="pluginTooltipEnabled && !hideUIElements"
      v-model="pluginTooltipEnabled"
      :style="pluginTooltipEnabled ? `top: ${pluginTooltipConfig.y}px; left: ${pluginTooltipConfig.x}px;` : undefined"
      :text="pluginTooltipConfig.text"
    />

    <TriggerDeadAnimal
      v-model="showDeadAnimalIncidentTrigger"
      :new-incident-payload="deadAnimalIncidentTriggerPayload"
      show-cancel-button
      @saved="
        () => {
          showDeadAnimalIncidentTrigger = false
          disableVisualLabelingMode()
          forceUpdateDeadAnimalPlugin()
        }
      "
      @cancel="
        () => {
          showDeadAnimalIncidentTrigger = false
          disableVisualLabelingMode()
        }
      "
    />
  </div>
</template>
